import { Order, QueryEntity } from '@datorama/akita';
import { compareEntities, compareEntity } from '@lycoris-nubila/ngx-lycoris';
import { isNil, reject, uniqBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { Module } from 'module/module';
import { ModuleQuestion } from 'module/page/question/module-question';
import { ModuleQuestionRepositoryPort } from 'module/page/question/port/module-question-repository.port';

import { CacheObservable } from '../../../../app-decorators';
import {
	ModuleQuestionState,
	ModuleQuestionStore,
} from './module-question.types';

export class ModuleQuestionRepositoryAdapter
	extends QueryEntity<ModuleQuestionState>
	implements ModuleQuestionRepositoryPort
{
	constructor(
		protected store: ModuleQuestionStore,
		private modules$: (ids: string[]) => Observable<Module[]>
	) {
		super(store);
	}

	@CacheObservable()
	public observeModuleQuestionsByIds(
		questionsId: string[]
	): Observable<ModuleQuestion[]> {
		return this.selectAll({
			filterBy: (entity) => questionsId.includes(entity.id),
			sortByOrder: Order.ASC,
			sortBy: 'position',
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document, this.modules$))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeModuleQuestionById(
		questionId: string
	): Observable<ModuleQuestion> {
		return this.selectEntity(questionId).pipe(
			map((document) => this.store.asEntity(document, this.modules$)),
			distinctUntilChanged(compareEntity),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async saveModuleQuestions(entities: ModuleQuestion[]): Promise<void> {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((e) => this.store.asDocument(e)),
				'id'
			)
		);
	}
}
